import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CreateDataFeedGrantInput,
  DataFeedGrant,
  DataFeedGrantIssueResult,
  PaginatedResult,
  SortDirection,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';
import { buildQuery } from '../../api/queryString.js';

export interface DataFeedGrantListParams {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDir?: SortDirection;
}

const dataFeedKeys = {
  all: ['data-feed-grants'] as const,
  list: (params: DataFeedGrantListParams) => ['data-feed-grants', 'list', params] as const,
};

export function useDataFeedGrants(
  params: DataFeedGrantListParams,
): UseQueryResult<PaginatedResult<DataFeedGrant>> {
  return useQuery({
    queryKey: dataFeedKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<DataFeedGrant>>(
        `/data-feed/grants${buildQuery({
          page: params.page,
          pageSize: params.pageSize,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
        })}`,
      ),
  });
}

export function useCreateDataFeedGrant(): UseMutationResult<
  DataFeedGrantIssueResult,
  unknown,
  CreateDataFeedGrantInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDataFeedGrantInput) =>
      api.post<DataFeedGrantIssueResult>('/data-feed/grants', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dataFeedKeys.all });
    },
  });
}

export function useRevokeDataFeedGrant(): UseMutationResult<DataFeedGrant, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<DataFeedGrant>(`/data-feed/grants/${id}/revoke`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dataFeedKeys.all });
    },
  });
}

export function useDeleteDataFeedGrant(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/data-feed/grants/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dataFeedKeys.all });
    },
  });
}
